import type { ComponentProps } from "react";
import { createContext, useContext } from "react";
import { tv } from "tailwind-variants";

import type { DraftCartItem } from "@/domains/careplan/models/types";
import { Card, Counter, Heading, type HeadingProps } from "@/ui";
import { formatAmount } from "@/utils/money";

const cartProductVariants = tv({
  slots: {
    root: undefined,
    name: "text-base font-semibold sm:text-base",
    image:
      "h-48 w-full rounded-2xl bg-product-media object-contain sm:aspect-square sm:h-auto sm:w-60 sm:object-cover",
    price: "flex items-baseline gap-2",
    priceAmount: "text-base font-semibold text-content",
    priceSuffix: "text-sm font-normal text-content-secondary",
    listPrice: "text-sm text-content-subtle line-through",
    quantity: "text-sm",
    shippingFrequency: "text-sm font-semibold",
  },
  variants: {
    noQuantity: {
      true: {
        root: "border-0",
      },
      false: {
        root: "border",
      },
    },
  },
});

const {
  image,
  listPrice,
  name,
  price,
  priceAmount,
  priceSuffix,
  quantity,
  root,
  shippingFrequency,
} = cartProductVariants();

const getUnitPrices = (product: DraftCartItem) => {
  if (product.source === "subscription") {
    return {
      unitPriceAsString: formatAmount(product.finalPrice),
      unitListPriceAsString: formatAmount(product.price),
    };
  }

  return product;
};

export type CartProductProps = {
  product: DraftCartItem;
} & ComponentProps<"div">;

type CartProductContextType = DraftCartItem;

const CartProductContext = createContext<CartProductContextType | null>(null);

export const useCartProductContext = () => {
  const product = useContext(CartProductContext);
  if (!product) {
    throw Error("useCartProductContext can only be used inside a CartProductContext.Provider");
  }

  return product;
};

const Name = ({ className, ...props }: HeadingProps) => {
  const product = useCartProductContext();

  return (
    <Heading className={name({ className })} {...props}>
      {product.shortName}
    </Heading>
  );
};

const Price = ({ className, ...props }: ComponentProps<"div">) => {
  const product = useCartProductContext();
  const { unitListPriceAsString, unitPriceAsString } = getUnitPrices(product);

  return (
    <div className={price({ className })} {...props}>
      <p className={priceAmount()}>
        ${unitPriceAsString}
        <span className={priceSuffix()}>/mo</span>
      </p>
      {unitListPriceAsString === unitPriceAsString ? null : (
        <p className={listPrice()}>
          <span className="sr-only">Retail price </span>${unitListPriceAsString}
        </p>
      )}
    </div>
  );
};

type QuantityProps = Omit<ComponentProps<"p">, "onChange"> & {
  disabled?: boolean;
  onChange: (quantity: number) => void;
};

const Quantity = ({ className, disabled, onChange, ...props }: QuantityProps) => {
  const product = useCartProductContext();

  return (
    <div className="flex items-center gap-4">
      <p className={quantity({ className })} {...props}>
        Quantity:
      </p>
      <Counter disabled={disabled} onChange={onChange} value={product.quantity} />
    </div>
  );
};

const ShippingFrequency = ({ className, ...props }: ComponentProps<"p">) => {
  return (
    <p className={shippingFrequency({ className })} {...props}>
      Ships monthly
    </p>
  );
};

const Image = ({ className }: ComponentProps<"img">) => {
  const product = useCartProductContext();

  return <img alt={product.name} className={image({ className })} src={product.image} />;
};

const Root = ({ children, className, product }: CartProductProps) => {
  return (
    <CartProductContext.Provider value={product}>
      <Card className={root({ className, noQuantity: product.quantity === 0 })}>{children}</Card>
    </CartProductContext.Provider>
  );
};

export const CartProduct = {
  Root,
  Name,
  Price,
  Image,
  Quantity,
  ShippingFrequency,
};
